import { Spinner } from '@getethos/ethos-design-system-v2';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import React, { useCallback, useMemo, useState } from 'react';
import { Control, useFieldArray, UseFormSetValue } from 'react-hook-form';
import { MdAdd } from 'react-icons/md';

import styles from '../styles.module.scss';
import { ETIAnswerV1, ETIFormData } from '../types';
import { generateSectionNumbering } from '../utils/numbering';
import QuestionForm from './QuestionForm';

export interface ETIFormProps {
  control: Control<Omit<ETIFormData, 'taskId' | 'status'>>;
  setValue?: UseFormSetValue<Omit<ETIFormData, 'taskId' | 'status'>>;
  className?: string;
  isDisabled: boolean;
  isReadOnly: boolean;
  isLoading: boolean;
  answers: ETIAnswerV1[];
  isVersion2: boolean;
  onReset?: () => void;
}

const ETIForm = ({
  control,
  setValue,
  className,
  isDisabled,
  isReadOnly,
  isLoading,
  answers,
  isVersion2,
  onReset,
}: ETIFormProps) => {
  const { fields, append, remove } = useFieldArray({
    name: 'questions',
    control,
  });

  // Trigger state to manually recalculate numbering when section field loses focus
  const [sectionRecalcTrigger, setSectionRecalcTrigger] = useState(0);

  // Callback to trigger numbering recalculation (called from section field onBlur)
  const handleSectionBlur = useCallback(() => {
    setSectionRecalcTrigger(prev => prev + 1);
  }, []);

  // Generate section-aware numbering
  // Only recalculates when questions are added/removed OR section field loses focus
  const numbering = useMemo(() => {
    const currentQuestions = control._getFieldArray('questions') || [];
    return generateSectionNumbering(currentQuestions as any);
  }, [fields.length, sectionRecalcTrigger]);

  const handleOnAddClick = useCallback(() => {
    append({
      question: '',
      questionType: 'text',
      section: '',
      optionsRaw: '',
      filterRaw: '',
      options: undefined,
      filter: undefined,
    });
  }, [append]);

  // Smart filter update when deleting questions
  const handleRemoveQuestion = useCallback(
    (indexToRemove: number) => {
      const currentQuestions = (control._getFieldArray('questions') ||
        []) as any[];
      const deletedQuestion = currentQuestions[indexToRemove];
      if (!deletedQuestion) return;

      const deletedSection = deletedQuestion.section ?? '';

      // Calculate the display number (1-based) of the deleted question within its section
      let deletedDisplayNumber = 0;
      for (let i = 0; i <= indexToRemove; i++) {
        if (currentQuestions[i]?.section === deletedSection) {
          deletedDisplayNumber++;
        }
      }

      // Collect all filter updates before applying them
      const filterUpdates: {
        index: number;
        filter: any;
        filterRaw: string;
      }[] = [];

      // Update all remaining questions' filters
      for (let i = 0; i < currentQuestions.length; i++) {
        if (i === indexToRemove) continue; // Skip the question being deleted

        const question = currentQuestions[i];

        // Only update filters for questions in the same section
        // and if filter is actually an object (not null, array, etc)
        if (
          question.section === deletedSection &&
          question.filter &&
          typeof question.filter === 'object' &&
          !Array.isArray(question.filter)
        ) {
          const updatedFilter = { ...question.filter };
          let hasChanges = false;

          for (const [key, value] of Object.entries(updatedFilter)) {
            // Parse the key as a number - skip if not a valid numeric key
            const refNum = parseInt(key, 10);
            if (isNaN(refNum)) continue; // Skip non-numeric keys

            if (refNum === deletedDisplayNumber) {
              // This filter references the deleted question - remove it
              delete updatedFilter[key];
              hasChanges = true;
            } else if (refNum > deletedDisplayNumber) {
              // Shift down display numbers that are after the deleted question
              delete updatedFilter[key];
              updatedFilter[refNum - 1] = value;
              hasChanges = true;
            }
            // else: keep unchanged (number < deleted display number)
          }

          if (hasChanges) {
            // Collect the update instead of applying immediately
            const isEmpty = Object.keys(updatedFilter).length === 0;
            filterUpdates.push({
              index: i,
              filter: isEmpty ? undefined : updatedFilter,
              filterRaw: isEmpty ? '' : JSON.stringify(updatedFilter),
            });
          }
        }
      }

      // Apply all filter updates in batch
      filterUpdates.forEach(update => {
        setValue?.(`questions.${update.index}.filter`, update.filter, {
          shouldDirty: true,
        });
        setValue?.(`questions.${update.index}.filterRaw`, update.filterRaw, {
          shouldDirty: true,
        });
      });

      // Now remove the question
      remove(indexToRemove);
    },
    [control, remove, setValue],
  );

  return (
    <div className={className}>
      <Table component={Paper}>
        <TableHead>
          <TableRow>
            <TableCell className={styles.NumberCell}>#</TableCell>
            <TableCell className={styles.QuestionTypeCell}>Type</TableCell>
            <TableCell className={styles.SectionCell}>Section</TableCell>
            <TableCell className={styles.Cell}>Question</TableCell>
            <TableCell className={styles.Cell}>Options</TableCell>
            <TableCell className={styles.Cell}>Filter</TableCell>
            {!isReadOnly ? (
              <TableCell className={styles.ActionsCell} align="center">
                Actions
              </TableCell>
            ) : !isVersion2 ? (
              <TableCell className={styles.Cell}>Answer</TableCell>
            ) : null}
          </TableRow>
        </TableHead>
        <TableBody>
          {fields.map((field, index) => (
            <QuestionForm
              onRemove={() => handleRemoveQuestion(index)}
              index={index}
              key={field.id}
              control={control}
              setValue={setValue}
              isDisabled={isDisabled}
              isReadOnly={isReadOnly}
              answers={answers}
              isVersion2={isVersion2}
              questionNumber={numbering[index]?.number}
              sectionStart={numbering[index]?.sectionStart}
              onSectionBlur={handleSectionBlur}
            />
          ))}
          {isLoading && (
            <TableRow>
              <TableCell align="center" colSpan={7}>
                <Spinner />
              </TableCell>
            </TableRow>
          )}
          {!isLoading && fields.length === 0 && (
            <TableRow>
              <TableCell align="center" colSpan={7}>
                No questions
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      {!isReadOnly && (
        <Box
          sx={{
            display: 'flex',
            gap: '8px',
            marginBlock: '1.5rem',
            marginRight: '0.5rem',
            justifyContent: 'flex-end',
          }}
        >
          {onReset && (
            <Button
              size="small"
              variant="outlined"
              color="error"
              disabled={isDisabled || fields.length === 0}
              onClick={onReset}
            >
              Reset Form
            </Button>
          )}
          <Button
            size="small"
            variant="contained"
            disabled={isDisabled}
            onClick={handleOnAddClick}
          >
            <MdAdd />
            Add Question
          </Button>
        </Box>
      )}
    </div>
  );
};

export default ETIForm;
